import { z } from "zod";
import type { RuleSetMeta } from "../types.js";

/**
 * AU-13 — Child Care Subsidy (CCS) calculator + backdated reclaim.
 *
 * The CCS payable per hour is, per the Family Assistance Act / DSS guide
 * §3.5.4: `applicable_percentage × min(hourly_fee, hourly_rate_cap)`, paid
 * for up to the family's `subsidised_hours_entitlement` per fortnight (set by
 * the activity test in the activity-test era, or the 3 Day Guarantee from
 * 5 Jan 2026). This engine models a *single child's* fortnightly entitlement;
 * a family with several children sums per-child results (and may attract the
 * higher second-child rate — see `childRank`).
 */

/** The four CCS care types — each carries its own hourly rate cap. */
export const CCS_CARE_TYPES = [
  "centre_based_day_care",
  "family_day_care",
  "outside_school_hours_care",
  "in_home_care",
] as const;
export type CcsCareType = (typeof CCS_CARE_TYPES)[number];

export const Au13InputSchema = z.object({
  /**
   * Combined annual adjusted taxable income for the family (single income if
   * not partnered). Sets the applicable-percentage tier.
   */
  familyIncome: z.number().min(0),
  /** Hours of subsidised care used per fortnight for THIS child (the engine
   * pays the lesser of this and the entitlement). */
  hoursUsed: z.number().min(0),
  /** Care type — selects the hourly rate cap. */
  careType: z.enum(CCS_CARE_TYPES),
  /** Actual hourly fee the service charges, in dollars. CCS pays the
   * applicable % of `min(fee, cap)`. */
  hourlyFee: z.number().min(0),
  /**
   * Recognised participation hours per fortnight (lower of the two partners,
   * per the activity test). Drives the subsidised-hours entitlement tier.
   */
  activityHours: z.number().min(0).default(0),
  /** True if the child attends school — selects the school-aged rate cap for
   * centre-based / OSHC (FDC and IHC ignore this). */
  schoolAged: z.boolean().default(false),
  /** Aboriginal or Torres Strait Islander child — always entitled to the top
   * hours tier regardless of activity. */
  indigenousChild: z.boolean().default(false),
  /**
   * 1 = eldest/only child (standard rate); 2 = a second-or-younger child in a
   * family with multiple children aged 5 or under, who may attract the higher
   * CCS rate. Defaults to 1.
   */
  childRank: z.union([z.literal(1), z.literal(2)]).default(1),
});

export type Au13Input = z.infer<typeof Au13InputSchema>;

export interface Au13Output {
  /** Applicable subsidy percentage as a fraction (e.g. 0.90 = 90%). */
  subsidyPercent: number;
  /** Whether the higher second-and-younger-child rate was applied. */
  higherRateApplied: boolean;
  /** Subsidised hours per fortnight the family is entitled to for this child. */
  subsidisedHoursEntitlement: number;
  /** Hours actually subsidised = min(hoursUsed, entitlement). */
  subsidisedHours: number;
  /** The hourly rate cap in force for this care type/age, in dollars. */
  cappedHourlyRate: number;
  /** The fee CCS is calculated on = min(hourlyFee, cappedHourlyRate). */
  subsidisedRate: number;
  /** Subsidy payable per hour = subsidyPercent × subsidisedRate. */
  subsidyPerHour: number;
  /** Total subsidy for the fortnight = subsidyPerHour × subsidisedHours. */
  subsidyPerFortnight: number;
  /** The out-of-pocket gap fee per fortnight (informational). */
  gapPerFortnight: number;
}

/**
 * One segment of a piecewise (stepped) standard-percentage schedule. Covers
 * income from `fromThreshold` up to the next segment's `fromThreshold` (or the
 * taper's `zeroThreshold` for the last one). A segment is either FLAT
 * (`taperStep` omitted → `fromPercent` throughout) or TAPERING (reduces 1 point
 * per `taperStep` dollars, starting at `fromPercent`). The pre-2023-reform CCS
 * schedule needs this: it had 50% and 20% flat bands between two tapers. The
 * post-reform schedule is a single linear taper and omits segments.
 */
export interface CcsTaperSegment {
  /** Inclusive lower income bound of this segment. */
  fromThreshold: number;
  /** Applicable percentage (fraction) at `fromThreshold`. */
  fromPercent: number;
  /** Dollars of income per 1-point reduction within the segment; omit for a
   * flat segment. */
  taperStep?: number;
}

/**
 * Standard applicable-percentage taper for one income regime. The current
 * (post-2023-reform) CCS is a single smooth linear taper: `maxPercent` up to
 * `maxThreshold`, then minus 1 point per `taperStep` dollars, hitting 0% at
 * `zeroThreshold`. The pre-reform schedule was STEPPED — supply `segments` to
 * describe it; the engine then evaluates those between `maxThreshold` and
 * `zeroThreshold` instead of the single linear ramp.
 */
export interface CcsTaper {
  /** Maximum applicable percentage as a fraction. */
  maxPercent: number;
  /** Income at/below which `maxPercent` applies. */
  maxThreshold: number;
  /** Income reduces the percentage by 1 point per this many dollars (linear
   * regime, or the first band's rate when `segments` is present). */
  taperStep: number;
  /** Income at/above which the percentage is 0. */
  zeroThreshold: number;
  /** Optional stepped schedule (pre-reform). When present it replaces the
   * single linear ramp between `maxThreshold` and `zeroThreshold`. */
  segments?: readonly CcsTaperSegment[];
}

/**
 * Higher-CCS-for-second-child schedule (in force from the 10 Jul 2023 reform
 * onward). A piecewise taper: 95% up to `topThreshold`, tapering to a floor,
 * a flat middle band, a second taper, then reverting to the standard rate.
 * Encoded as the canonical DSS breakpoints so we can interpolate exactly.
 */
export interface HigherRateSchedule {
  /** Income at/below which the top higher rate applies. */
  topThreshold: number;
  /** Top higher percentage (fraction, e.g. 0.95). */
  topPercent: number;
  /** First taper step ($ per 1%). */
  taperStep: number;
  /** Flat-band percentage after the first taper (e.g. 0.80). */
  midPercent: number;
  /** Income at which the flat mid band starts. */
  midFromThreshold: number;
  /** Income at which the flat mid band ends / second taper begins. */
  midToThreshold: number;
  /** Floor percentage the second taper bottoms out at (e.g. 0.50). */
  floorPercent: number;
  /** Income at/above which higher rates stop and the standard rate applies. */
  revertThreshold: number;
}

/** Activity-test tier: an activity-hours band → subsidised hours/fortnight. */
export interface ActivityTier {
  /** Lower bound of fortnightly activity hours, inclusive. */
  fromHours: number;
  /** Upper bound, inclusive. `null` = unbounded (top tier). */
  toHours: number | null;
  /** Subsidised hours per fortnight this tier grants. */
  subsidisedHours: number;
}

/** How subsidised-hours entitlement is determined for a rule period. */
export interface CcsHoursRules {
  /** "activity_test" (pre-3DG) or "three_day_guarantee" (from 5 Jan 2026). */
  mode: "activity_test" | "three_day_guarantee";
  /**
   * Activity-test era only: the tiers, plus the low-income safety-net hours
   * (granted to families under the max-percentage threshold who fail the
   * activity test). Empty for the 3DG mode.
   */
  activityTiers: readonly ActivityTier[];
  /** Activity-test era: hours for low-income families failing the test. */
  lowIncomeSafetyNetHours: number;
  /** 3DG era: the guaranteed baseline (72) every eligible family receives. */
  guaranteeHours: number;
  /** 3DG era: the upper tier (100) for 48+ activity hours. */
  highTierHours: number;
  /** 3DG era: fortnightly activity hours that unlock `highTierHours`. */
  highTierActivityThreshold: number;
  /** Hours an Aboriginal/Torres Strait Islander child always receives. */
  indigenousHours: number;
}

/** Hourly rate caps per care type, in dollars (DSS guide §3.5.3 / §1.1.H.20). */
export interface CcsRateCaps {
  centreBasedBelowSchool: number;
  centreBasedSchoolAged: number;
  /** OSHC below-school-age cap (rarely used; OSHC is school-aged care). */
  oshcBelowSchool: number;
  oshcSchoolAged: number;
  /** FDC applies one cap regardless of school age. */
  familyDayCare: number;
  /** IHC cap is per family, not per child. */
  inHomeCarePerFamily: number;
}

/** A complete CCS ruleset for one rule *period* (a FY, or a sub-period of a FY
 * that straddles a mid-year reform). */
export interface Au13RuleSet extends RuleSetMeta {
  /** Stable key for this period in the registry, e.g. "2023-24:post-reform". */
  periodKey: string;
  /** Human label for the period, surfaced in copy/audit. */
  periodLabel: string;
  /** Inclusive start of THIS rule period (may be mid-FY). */
  periodFrom: string;
  /** Inclusive end of THIS rule period (may be mid-FY). */
  periodTo: string;
  standardTaper: CcsTaper;
  /** Higher second-child schedule, or null if not in force this period. */
  higherRate: HigherRateSchedule | null;
  hours: CcsHoursRules;
  rateCaps: CcsRateCaps;
}
